import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import CopyButton from './CopyButton';
import { buttonClass, fieldClass, labelClass, mutedClass, tabClass } from '../uiStyles';

// btoa/atob only speak Latin-1, so text goes through UTF-8 by hand — otherwise
// every "ў" and every emoji would throw or come back mangled.
const encodeBase64 = (text, urlSafe) => {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  const base64 = btoa(binary);
  return urlSafe ? base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : base64;
};

const decodeBase64 = (value) => {
  const normalized = value.trim().replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
};

const MODES = ['encode', 'decode'];

const Base64Tool = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [mode, setMode] = useState('encode');
  const [text, setText] = useState('');
  const [urlSafe, setUrlSafe] = useState(false);

  let output = '';
  let error = null;
  if (text) {
    try {
      output = mode === 'encode' ? encodeBase64(text, urlSafe) : decodeBase64(text);
    } catch {
      error = t('useful.base64.invalid');
    }
  }

  const swap = () => {
    setMode((current) => (current === 'encode' ? 'decode' : 'encode'));
    if (output) setText(output);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {MODES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            className={tabClass(isDarkMode, mode === option)}
          >
            {t(`useful.base64.${option}`)}
          </button>
        ))}
        {mode === 'encode' && (
          <label className={`flex items-center gap-2 text-sm ml-2 ${mutedClass(isDarkMode)}`}>
            <input
              type="checkbox"
              checked={urlSafe}
              onChange={() => setUrlSafe((value) => !value)}
              className={isDarkMode ? 'accent-[#00cc7e]' : 'accent-green-600'}
            />
            {t('useful.base64.urlSafe')}
          </label>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="base64-input">
            {t('useful.base64.input')}
          </label>
          <textarea
            id="base64-input"
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={8}
            spellCheck={false}
            placeholder={mode === 'encode' ? 'Salom dunyo' : 'U2Fsb20gZHVueW8='}
            className={`${fieldClass(isDarkMode)} resize-y font-mono text-sm`}
          />
        </div>
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="base64-output">
            {t('useful.result')}
          </label>
          <textarea
            id="base64-output"
            value={error || output}
            readOnly
            rows={8}
            className={`${fieldClass(isDarkMode)} resize-y font-mono text-sm ${error ? 'text-red-500' : ''}`}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <CopyButton value={error ? '' : output} />
        <button type="button" onClick={swap} className={`${buttonClass(isDarkMode)} inline-flex items-center gap-2`}>
          <FaExchangeAlt /> {t('useful.swap')}
        </button>
        <button type="button" onClick={() => setText('')} className={buttonClass(isDarkMode)} disabled={!text}>
          {t('useful.clear')}
        </button>
      </div>
    </div>
  );
};

export default Base64Tool;
